import { useState, useEffect, useMemo } from 'react';
import { useInvoices } from '../../hooks/useData';
import { QuickUploadButton } from '../../components/shared/FileUpload';
import { api, getCurrentMonth, formatCurrency } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Trash2, FileText, Camera, ArrowUp, ArrowDown, AlertTriangle, Brain, TrendingDown, ShoppingCart, Loader2, X, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Package, ChefHat, Wine } from 'lucide-react';
import { toast } from 'sonner';
import RetoursMarchandiseSection from '../../components/shared/RetoursMarchandise';

const MONTHS_FR = ['', 'Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'];

const ScoreBadge = ({ score }) => {
  const color = score >= 75 ? 'text-lemas-green' : score >= 50 ? 'text-yellow-400' : 'text-lemas-danger';
  const bg = score >= 75 ? 'bg-lemas-green/10' : score >= 50 ? 'bg-yellow-400/10' : 'bg-red-500/10';
  return (
    <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full ${bg}`}>
      <span className={`font-mono text-xl font-bold ${color}`}>{score}</span>
      <span className="text-xs text-[rgb(var(--lm-text-secondary))]">/100</span>
    </div>
  );
};

const VerdictTag = ({ verdict }) => {
  const map = {
    'Bon prix': 'bg-lemas-green/15 text-lemas-green',
    'Correct': 'bg-blue-500/15 text-blue-400',
    'Cher': 'bg-orange-500/15 text-orange-400',
    'Tres cher': 'bg-red-500/15 text-red-400',
  };
  const cls = map[verdict] || 'bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text-secondary))]';
  return <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cls}`}>{verdict}</span>;
};

const DeepAnalysisPanel = ({ analysis, onClose }) => {
  const [expandedProduct, setExpandedProduct] = useState(null);
  if (!analysis) return null;
  return (
    <div className="space-y-5 animate-slide-up">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Brain className="text-purple-400" size={20} />
          <h4 className="font-title text-lg">Analyse IA</h4>
        </div>
        <div className="flex items-center gap-3">
          <ScoreBadge score={analysis.score_facture} />
          {onClose && <button onClick={onClose} className="text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]"><X size={16} /></button>}
        </div>
      </div>
      <div className="bg-lemas-bg/60 rounded-lg p-4 border border-[rgb(var(--lm-border))]">
        <p className="text-sm font-medium text-[rgb(var(--lm-text))]/90 mb-1">{analysis.verdict}</p>
        <p className="text-sm text-[rgb(var(--lm-text-secondary))] leading-relaxed">{analysis.resume_chef}</p>
      </div>
      {analysis.economies_possibles?.total_estime > 0 && (
        <div className="bg-lemas-green/5 border border-lemas-green/20 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <TrendingDown className="text-lemas-green" size={16} />
            <span className="text-sm font-medium text-lemas-green">Economies possibles : {formatCurrency(analysis.economies_possibles.total_estime)}</span>
          </div>
          <div className="space-y-2">
            {analysis.economies_possibles.details?.map((e, i) => (
              <div key={i} className="flex items-start justify-between text-sm bg-lemas-bg/40 rounded p-2.5">
                <div className="flex-1"><span className="font-medium">{e.produit}</span><p className="text-xs text-[rgb(var(--lm-text-secondary))] mt-0.5">{e.comment}</p></div>
                <span className="font-mono text-lemas-green shrink-0 ml-2">-{formatCurrency(e.economie)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {analysis.produits_analyse?.length > 0 && (
        <div>
          <h5 className="text-xs uppercase tracking-wider text-[rgb(var(--lm-text-secondary))] mb-2">Analyse par produit</h5>
          <div className="space-y-1.5">
            {analysis.produits_analyse.map((p, i) => (
              <div key={i} className="bg-lemas-bg/40 rounded-lg overflow-hidden">
                <button onClick={() => setExpandedProduct(expandedProduct === i ? null : i)} className="w-full flex items-center justify-between p-3 text-left hover:bg-white/[0.02] transition-colors">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span className="text-sm font-medium truncate">{p.produit}</span>
                    <VerdictTag verdict={p.verdict} />
                  </div>
                  <div className="flex items-center gap-2 shrink-0 ml-2">
                    <span className="font-mono text-xs">{p.prix_paye}€ vs ~{p.prix_marche_estime}€/{p.unite}</span>
                    {expandedProduct === i ? <ChevronUp size={14} className="text-[rgb(var(--lm-text-muted))]" /> : <ChevronDown size={14} className="text-[rgb(var(--lm-text-muted))]" />}
                  </div>
                </button>
                {expandedProduct === i && (
                  <div className="px-3 pb-3 space-y-2 animate-slide-up">
                    {p.ecart_pct !== 0 && (
                      <div className="flex items-center gap-1.5 text-xs">
                        {p.ecart_pct > 0 ? <ArrowUp size={12} className="text-lemas-danger" /> : <ArrowDown size={12} className="text-lemas-green" />}
                        <span className={p.ecart_pct > 0 ? 'text-lemas-danger' : 'text-lemas-green'}>{p.ecart_pct > 0 ? '+' : ''}{Math.round(p.ecart_pct)}% vs prix marche</span>
                      </div>
                    )}
                    {p.ou_trouver_moins_cher && <div className="flex items-start gap-1.5 text-xs text-[rgb(var(--lm-text-secondary))]"><ShoppingCart size={12} className="mt-0.5 shrink-0 text-purple-400" /><span>{p.ou_trouver_moins_cher}</span></div>}
                    {p.conseil && <p className="text-xs text-[rgb(var(--lm-text-secondary))] italic">{p.conseil}</p>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {analysis.conseils_negociation?.length > 0 && (
        <div>
          <h5 className="text-xs uppercase tracking-wider text-[rgb(var(--lm-text-secondary))] mb-2">Conseils de negociation</h5>
          <div className="space-y-1.5">
            {analysis.conseils_negociation.map((c, i) => (
              <div key={i} className="text-sm text-[rgb(var(--lm-text-secondary))] bg-lemas-bg/40 rounded p-2.5 flex items-start gap-2">
                <span className="text-lemas-green shrink-0 mt-0.5">-</span><span>{c}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const ChefFactures = () => {
  const [mois, setMois] = useState(getCurrentMonth());
  const { invoices, loading, refetch } = useInvoices(mois);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [priceAlerts, setPriceAlerts] = useState([]);
  const [lastScanAlerts, setLastScanAlerts] = useState([]);
  const [deepAnalysis, setDeepAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [viewMode, setViewMode] = useState('fournisseur'); // 'fournisseur' or 'date'

  const navigateMonth = (dir) => {
    const [yy, mm] = mois.split('-').map(Number);
    let nm = mm + dir, ny = yy;
    if (nm > 12) { nm = 1; ny++; }
    if (nm < 1) { nm = 12; ny--; }
    setMois(`${ny}-${String(nm).padStart(2, '0')}`);
  };
  const [yy, mm] = mois.split('-').map(Number);

  const fetchPriceAlerts = async () => {
    try { const res = await api.get('/price-alerts'); setPriceAlerts(res.data); } catch {}
  };
  useEffect(() => { fetchPriceAlerts(); }, []);

  const handleUploadSuccess = (data) => {
    refetch();
    setSelectedInvoice(data);
    setDeepAnalysis(null);
    fetchPriceAlerts();
    if (data?.produits?.length > 0) checkLastScanPrices(data);
  };

  const checkLastScanPrices = async (invoice) => {
    const alerts = [];
    for (const prod of invoice.produits || []) {
      if (prod.prix_unitaire > 0 && prod.nom) {
        try {
          const res = await api.get(`/price-history/${encodeURIComponent(prod.nom)}`);
          const history = res.data;
          if (history.length >= 2) {
            const prev = history[1];
            const ecart = ((prod.prix_unitaire - prev.prix) / prev.prix) * 100;
            if (Math.abs(ecart) > 5) alerts.push({ produit: prod.nom, ancien: prev.prix, nouveau: prod.prix_unitaire, ecart: Math.round(ecart * 10) / 10, unite: prod.unite });
          }
        } catch {}
      }
    }
    if (alerts.length > 0) setLastScanAlerts(alerts);
  };

  const handleDeepAnalyze = async (invoice) => {
    if (invoice.deep_analysis) { setDeepAnalysis(invoice.deep_analysis); return; }
    setAnalyzing(true);
    try {
      const res = await api.post(`/deep-analyze-invoice/${invoice.id}`);
      setDeepAnalysis(res.data);
      invoice.deep_analysis = res.data;
      toast.success('Analyse terminee');
    } catch { toast.error("Erreur analyse IA"); }
    setAnalyzing(false);
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/invoices/${id}`); toast.success('Facture supprimee'); refetch(); if (selectedInvoice?.id === id) setSelectedInvoice(null); } catch { toast.error('Erreur'); }
  };

  // Group invoices by fournisseur
  const groupedByFournisseur = useMemo(() => {
    const groups = {};
    invoices.forEach(inv => {
      const key = inv.fournisseur || 'Autre';
      if (!groups[key]) groups[key] = { fournisseur: key, factures: [], total_ht: 0, total_ttc: 0, nb_produits: 0 };
      groups[key].factures.push(inv);
      const detail = (inv.cuisine_ht || 0) + (inv.boissons_ht || 0) + (inv.non_alimentaire_ht || 0);
      groups[key].total_ht += detail > 0 ? detail : (inv.total_ht || 0);
      groups[key].total_ttc += inv.total_ttc || 0;
      groups[key].nb_produits += inv.produits?.length || 0;
    });
    return Object.values(groups).sort((a, b) => b.total_ttc - a.total_ttc);
  }, [invoices]);

  // Totals
  const totalTTC = invoices.reduce((s, i) => s + (i.total_ttc || 0), 0);
  const totalCuisine = invoices.reduce((s, i) => s + (i.cuisine_ht || 0), 0);
  const totalBoissons = invoices.reduce((s, i) => s + (i.boissons_ht || 0), 0);
  const totalNonAlim = invoices.reduce((s, i) => s + (i.non_alimentaire_ht || 0), 0);
  const totalProduits = invoices.reduce((s, i) => s + (i.produits?.length || 0), 0);

  const hausse = priceAlerts.filter(a => a.tendance === 'hausse');
  const baisse = priceAlerts.filter(a => a.tendance === 'baisse');

  return (
    <div className="space-y-4 animate-slide-up" data-testid="chef-factures">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-[rgb(var(--lm-text))] flex items-center gap-2">
          <Camera size={20} className="text-[rgb(var(--lm-green))]" /> Factures
        </h2>
        <div className="flex items-center gap-2">
          <button onClick={() => navigateMonth(-1)} className="p-1.5 rounded-lg hover:bg-[rgb(var(--lm-surface))]" data-testid="chef-factures-month-prev">
            <ChevronLeft size={16} className="text-[rgb(var(--lm-text-muted))]" />
          </button>
          <span className="text-sm font-medium text-[rgb(var(--lm-text))] min-w-[120px] text-center">{MONTHS_FR[mm]} {yy}</span>
          <button onClick={() => navigateMonth(1)} className="p-1.5 rounded-lg hover:bg-[rgb(var(--lm-surface))]" data-testid="chef-factures-month-next">
            <ChevronRight size={16} className="text-[rgb(var(--lm-text-muted))]" />
          </button>
        </div>
      </div>

      {/* Scanner */}
      <QuickUploadButton onUpload={handleUploadSuccess} endpoint="/analyze-invoice" source="chef" variant="green" />

      {/* Last scan alerts */}
      {lastScanAlerts.length > 0 && (
        <div className="lm-card p-4 border-orange-400/30 bg-orange-400/5" data-testid="last-scan-alerts">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className="text-orange-400" size={16} />
              <span className="font-medium text-orange-400 text-sm">Prix modifies</span>
            </div>
            <button onClick={() => setLastScanAlerts([])} className="text-xs text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]">Fermer</button>
          </div>
          <div className="space-y-1">
            {lastScanAlerts.map((a, i) => (
              <div key={i} className={`flex items-center justify-between p-2 rounded text-sm ${a.ecart > 0 ? 'bg-red-900/10' : 'bg-green-900/10'}`}>
                <div className="flex items-center gap-2">
                  {a.ecart > 0 ? <ArrowUp size={14} className="text-lemas-danger" /> : <ArrowDown size={14} className="text-lemas-green" />}
                  <span>{a.produit}</span>
                </div>
                <span className={`font-mono text-xs font-bold ${a.ecart > 0 ? 'text-lemas-danger' : 'text-lemas-green'}`}>{a.ecart > 0 ? '+' : ''}{a.ecart}%</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        <div className="lm-card p-3 text-center">
          <p className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">Factures</p>
          <p className="font-mono text-xl text-[rgb(var(--lm-green))]">{invoices.length}</p>
        </div>
        <div className="lm-card p-3 text-center">
          <p className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">Total TTC</p>
          <p className="font-mono text-lg">{formatCurrency(totalTTC)}</p>
        </div>
        <div className="lm-card p-3 text-center">
          <p className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">Articles</p>
          <p className="font-mono text-lg">{totalProduits}</p>
        </div>
        <div className="lm-card p-3 text-center">
          <p className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">Fournisseurs</p>
          <p className="font-mono text-lg">{groupedByFournisseur.length}</p>
        </div>
      </div>

      {/* Repartition */}
      {(totalCuisine > 0 || totalBoissons > 0 || totalNonAlim > 0) && (
        <div className="lm-card p-3">
          <div className="flex gap-4 text-xs">
            {totalCuisine > 0 && <div className="flex items-center gap-1.5"><ChefHat size={12} style={{ color: 'rgb(var(--lm-green))' }} /><span className="font-mono">{formatCurrency(totalCuisine)}</span></div>}
            {totalBoissons > 0 && <div className="flex items-center gap-1.5"><Wine size={12} style={{ color: 'rgb(100, 149, 237)' }} /><span className="font-mono">{formatCurrency(totalBoissons)}</span></div>}
            {totalNonAlim > 0 && <div className="flex items-center gap-1.5"><Package size={12} style={{ color: 'rgb(var(--lm-orange))' }} /><span className="font-mono">{formatCurrency(totalNonAlim)}</span></div>}
          </div>
        </div>
      )}

      {/* View toggle */}
      {invoices.length > 0 && (
        <div className="flex gap-1">
          <button onClick={() => setViewMode('fournisseur')} className={`text-xs px-3 py-1.5 rounded-lg transition-colors ${viewMode === 'fournisseur' ? 'bg-[rgb(var(--lm-green)/0.15)] text-[rgb(var(--lm-green))]' : 'text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]'}`} data-testid="view-by-fournisseur">Par fournisseur</button>
          <button onClick={() => setViewMode('date')} className={`text-xs px-3 py-1.5 rounded-lg transition-colors ${viewMode === 'date' ? 'bg-[rgb(var(--lm-green)/0.15)] text-[rgb(var(--lm-green))]' : 'text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]'}`} data-testid="view-by-date">Par date</button>
        </div>
      )}

      {/* Grouped by Fournisseur */}
      {viewMode === 'fournisseur' && groupedByFournisseur.map(group => (
        <div key={group.fournisseur} className="lm-card overflow-hidden" data-testid={`fournisseur-group-${group.fournisseur}`}>
          <div className="p-3 flex items-center justify-between border-b border-[rgb(var(--lm-border)/0.2)]">
            <div className="flex items-center gap-2">
              <span className="font-medium text-sm">{group.fournisseur}</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text-muted))] font-mono">{group.factures.length}</span>
              {group.nb_produits > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 font-mono">{group.nb_produits} art.</span>}
            </div>
            <span className="font-mono text-sm text-[rgb(var(--lm-green))]">{formatCurrency(group.total_ttc)}</span>
          </div>
          <div className="divide-y divide-[rgb(var(--lm-border)/0.1)]">
            {group.factures.sort((a, b) => (a.date || '').localeCompare(b.date || '')).map(inv => (
              <div key={inv.id} className="flex items-center justify-between px-3 py-2 hover:bg-[rgb(var(--lm-surface)/0.3)] transition-colors cursor-pointer group" onClick={() => { setSelectedInvoice(inv); setDeepAnalysis(inv.deep_analysis || null); }}>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-xs text-[rgb(var(--lm-text-muted))]">{inv.date?.slice(5)}</span>
                  {inv.produits?.length > 0 ? (
                    <span className="text-[10px] text-emerald-400">{inv.produits.length} art.</span>
                  ) : (
                    <span className="text-[10px] text-amber-400">0 art.</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs">{formatCurrency(inv.total_ttc)}</span>
                  <div className="opacity-0 group-hover:opacity-100 flex gap-0.5 transition-opacity">
                    <button onClick={(e) => { e.stopPropagation(); handleDeepAnalyze(inv); }} className="p-1 text-purple-400 hover:text-purple-300"><Brain size={12} /></button>
                    <button onClick={(e) => { e.stopPropagation(); handleDelete(inv.id); }} className="p-1 text-red-400 hover:text-red-300"><Trash2 size={12} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* View by date */}
      {viewMode === 'date' && invoices.length > 0 && (
        <div className="lm-card overflow-hidden">
          <div className="divide-y divide-[rgb(var(--lm-border)/0.1)]">
            {[...invoices].sort((a, b) => (b.date || '').localeCompare(a.date || '')).map(inv => (
              <div key={inv.id} className="flex items-center justify-between px-3 py-2.5 hover:bg-[rgb(var(--lm-surface)/0.3)] transition-colors cursor-pointer group" onClick={() => { setSelectedInvoice(inv); setDeepAnalysis(inv.deep_analysis || null); }}>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-xs text-[rgb(var(--lm-text-muted))] w-[50px]">{inv.date?.slice(5)}</span>
                  <span className="text-sm font-medium">{inv.fournisseur}</span>
                  {inv.produits?.length > 0 ? (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 font-mono">{inv.produits.length} art.</span>
                  ) : (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-400">0 art.</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm text-[rgb(var(--lm-green))]">{formatCurrency(inv.total_ttc)}</span>
                  <div className="opacity-0 group-hover:opacity-100 flex gap-0.5 transition-opacity">
                    <button onClick={(e) => { e.stopPropagation(); handleDeepAnalyze(inv); }} className="p-1 text-purple-400 hover:text-purple-300"><Brain size={12} /></button>
                    <button onClick={(e) => { e.stopPropagation(); handleDelete(inv.id); }} className="p-1 text-red-400 hover:text-red-300"><Trash2 size={12} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {invoices.length === 0 && !loading && (
        <div className="lm-card p-8 text-center">
          <Camera size={32} className="mx-auto mb-3 text-[rgb(var(--lm-text-muted))]" />
          <p className="text-sm text-[rgb(var(--lm-text-muted))]">Aucune facture ce mois</p>
          <p className="text-xs text-[rgb(var(--lm-text-muted))] mt-1">Scannez votre premiere facture</p>
        </div>
      )}

      {/* Price alerts */}
      {hausse.length > 0 && (
        <div className="lm-card p-3 border-red-500/20">
          <div className="flex items-center gap-2 mb-2">
            <ArrowUp className="text-lemas-danger" size={14} />
            <span className="text-xs font-medium text-red-400">{hausse.length} produit(s) en hausse</span>
          </div>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {hausse.map((a, i) => (
              <div key={i} className="flex items-center justify-between text-xs p-1.5 rounded bg-red-900/5">
                <span className="truncate mr-2">{a.produit}</span>
                <span className="font-mono text-red-400 font-bold shrink-0">+{a.ecart_pct}%</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Retours marchandise */}
      <RetoursMarchandiseSection source="chef" />

      {/* Invoice Detail Modal */}
      {selectedInvoice && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => { setSelectedInvoice(null); setDeepAnalysis(null); }}>
          <div className="bg-[rgb(var(--lm-surface))] rounded-xl p-5 max-w-lg w-full max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="font-title text-xl">{selectedInvoice.fournisseur}</h3>
                <p className="text-sm text-[rgb(var(--lm-text-secondary))]">{selectedInvoice.date}</p>
              </div>
              <span className="font-mono text-2xl text-[rgb(var(--lm-green))]">{formatCurrency(selectedInvoice.total_ttc)}</span>
            </div>

            <div className="grid grid-cols-3 gap-2 mb-4">
              <div className="text-center bg-lemas-bg/50 rounded-lg p-2">
                <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">Cuisine</p>
                <p className="font-mono text-sm">{formatCurrency(selectedInvoice.cuisine_ht)}</p>
              </div>
              <div className="text-center bg-lemas-bg/50 rounded-lg p-2">
                <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">Boissons</p>
                <p className="font-mono text-sm">{formatCurrency(selectedInvoice.boissons_ht)}</p>
              </div>
              <div className="text-center bg-lemas-bg/50 rounded-lg p-2">
                <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">Non-alim</p>
                <p className="font-mono text-sm">{formatCurrency(selectedInvoice.non_alimentaire_ht)}</p>
              </div>
            </div>

            {selectedInvoice.produits?.length > 0 ? (
              <div>
                <h4 className="text-xs uppercase tracking-wider text-[rgb(var(--lm-text-secondary))] mb-2">Produits ({selectedInvoice.produits.length})</h4>
                <div className="space-y-1 max-h-52 overflow-y-auto">
                  {selectedInvoice.produits.map((p, i) => (
                    <div key={i} className="flex justify-between text-sm bg-lemas-bg/50 rounded p-2">
                      <div className="flex-1">
                        <span className="font-medium">{p.nom}</span>
                        <span className="text-[10px] text-[rgb(var(--lm-text-muted))] ml-1">[{p.categorie}]</span>
                      </div>
                      <span className="font-mono text-xs shrink-0">{p.quantite} {p.unite} x {formatCurrency(p.prix_unitaire)} = <span className="text-[rgb(var(--lm-green))]">{formatCurrency(p.montant_ht)}</span></span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="p-4 rounded-lg border border-amber-500/20 bg-amber-500/5 text-center">
                <Camera className="mx-auto mb-2 text-amber-400" size={20} />
                <p className="text-xs text-amber-400">Aucun produit extrait</p>
              </div>
            )}

            {selectedInvoice.alertes?.length > 0 && (
              <div className="mt-3 space-y-1">
                {selectedInvoice.alertes.map((a, i) => (
                  <div key={i} className="text-xs text-amber-400 bg-amber-500/10 rounded p-2">{a}</div>
                ))}
              </div>
            )}

            {/* Deep AI Analysis */}
            <div className="mt-4 pt-4 border-t border-[rgb(var(--lm-border))]">
              {!deepAnalysis && !analyzing && (
                <Button onClick={() => handleDeepAnalyze(selectedInvoice)} className="w-full bg-purple-600/80 hover:bg-purple-600 text-[rgb(var(--lm-text))]" data-testid="deep-analyze-btn">
                  <Brain size={16} className="mr-2" /> Analyser les prix
                </Button>
              )}
              {analyzing && (
                <div className="flex items-center justify-center gap-3 py-6 text-purple-400">
                  <Loader2 size={20} className="animate-spin" /><span className="text-sm">Analyse en cours...</span>
                </div>
              )}
              {deepAnalysis && <DeepAnalysisPanel analysis={deepAnalysis} onClose={() => setDeepAnalysis(null)} />}
            </div>

            <Button onClick={() => { setSelectedInvoice(null); setDeepAnalysis(null); }} className="w-full mt-4 bg-[rgb(var(--lm-green))] text-lemas-bg">Fermer</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChefFactures;
